/**
 * @file     iframe-common.js
 *           Common code used to initialize iframe content in portal.
 *            - make hyperlinks to "vite routes" cooperate with SPA
 *            - add a document.body "ready" attribute which fires after the hyperlinks are ready and all
 *              style elements have loaded
 *            - make moveable-block elements cooperate with Jnu databinding
 *            - make esc key click Jnu close button
 * @author   Wes Garland, wes@distributive.network
 * @date     Sep 2025
 *
 * @note     Debugging tip: the `module` symbol is from dcp-dom-tk
 */
var p$phase1;
window.addEventListener('load', iframeCommonPhase2);
window.addEventListener('DOMContentLoaded', () => p$phase1 = iframeCommonPhase1());
const p$domTk = import(dcpConfig.cdn.location.resolve('/dcp-dom-tk.js'));
var domTk;
var __m = {}; /* module exports from phase 1 */

p$domTk.then(() => {
  modules.loaders.css(top.document.querySelector('LINK[rel="stylesheet"][href^="/assets/branding.css?"]')?.href);
});

export function copyToClipboard(text)
{
  if (window.isSecureContext)
    navigator.clipboard.writeText(text)
  else
  {
    const dummy = document.createElement('TEXTAREA');
    dummy.style = 'opacity: 0; position: absolute; top: 0; left: 0; float: left; height: 0; width: 0;';
    dummy.textContent = text;
    document.body.appendChild(dummy);
    dummy.select();
    document.execCommand('copy');
    document.body.removeChild(dummy);
  }
}

/**
 * Phase 1 init: document has been parsed but not all deps loaded
 */
async function iframeCommonPhase1()
{
  /* Make anchors which have an image but no hyperlinks into clickable bookmark links. */
  for (let anchor of document.querySelectorAll('A[name]:not([href]):has(IMG)'))
  {
    const url = new URL(top.location.href);
    url.hash = anchor.getAttribute('name');
    anchor.setAttribute('title', url.href);
    anchor.addEventListener('click', () => {
      copyToClipboard(url.href);
      top.location.hash = url.hash;
    });
  }

  /* Rewrite hyperlinks which look like Vite routes and which do not already have click handlers and
   * and which do not target another frame to use navigateTo to change the page. This preserves the
   * authoring experience but keeps the history correct so that SPA interopt is seamless for the user.
   *
   * In the case where the hyperlink exactly matches a hyperlink in the outer frame's sidebar, that
   * hyperlink will get the "hightlight" class whiler hovering over the link in the iframe.
   */
  for (let anchor of document.querySelectorAll('A[href^="/"]:not([target]):not([onclick])'))
  {
    const href = anchor.getAttribute('href');
    if (!/^\/[a-z]+($|&)/.test(href))
      continue;
    anchor.addEventListener('click', anchorClickHandler);
    const sidenavLi = top.document.querySelector(`NAV#sidebar LI:has(A[href="${href}"])`);
    if (sidenavLi)
    {
      anchor.addEventListener('pointerenter', () => sidenavLi.classList.add   ('highlight'));
      anchor.addEventListener('pointerleave', () => sidenavLi.classList.remove('highlight'));
      anchor.addEventListener('pointerdown',  () => sidenavLi.classList.remove('highlight'));
      anchor.addEventListener('beforeunload', () => sidenavLi.classList.remove('highlight'));
    }
  }

  if (document.title && window !== top)
    top.document.title = document.title;

  document.body.setAttribute('load-phase1', true);

  /* Cache busting safe here because nobody else can load these modules */
  await import('./tfoot-nav.js?cache-key=DCP_CACHE_KEY');
  __m.jnuDatabinding = await import(dcpConfig.cdn.location.resolve('/components/jnu-databinding.js?cache-key=DCP_CACHE_KEY'));
  __m.portalWallet   = await import(dcpConfig.cdn.location.resolve('/components/portal-wallet.js?cache-key=DCP_CACHE_KEY'));
  import(dcpConfig.cdn.location.resolve('/components/gdpr-gtag.js')); /* Side effect: define modules */
}

/**
 * Phase 2 init: document and deps have been loaded and parsed
 */
async function iframeCommonPhase2()
{
  await p$phase1;
  Object.assign(modules, __m);
  if (modules.moveableBlock)
    modules.moveableBlock.noDragAttribs.push('jnu-field');

  $$('JNU-RECORD-SET').forEach(recordSet => {
    recordSet.on('choose', () => window.on('keypress', choiceKeyHandler));
    recordSet.on('choose', () => window.on('keyup',    choiceKeyHandler));
  });
  $$('JNU-RECORD-SET').forEach(recordSet => {
    recordSet.on('unchoose', () => window.off('keypress', choiceKeyHandler));
    recordSet.on('unchoose', () => window.off('keyup',    choiceKeyHandler));
  });

  if (top !== window)
  {
    if (top.location.hash)
    {
      const anchor = $(`A[name="${top.location.hash.slice(1)}"]`);
      if (anchor)
      {
        const rs = anchor.closest('jnu-record-set');
        if (rs)
          rs.addEventListener('ready', () => anchor.scrollIntoView());
        setTimeout(() => anchor.scrollIntoView(), 500);
      }
    }

    top.addEventListener('hashchange', () => {
      const anchor = $(`A[name="${top.location.hash.slice(1)}"]`);
      if (anchor)
        anchor.scrollIntoView();
    });
  }

  document.body.setAttribute('ready', true);
}

/**
 * Keyboard shortcuts which are applied to Jnu elements, whether or not one is chosen or not.
 *
 * enter => do not propagate inside of contenteditable, just blur
 */
function globalKeyHandler(ev)
{
  /* Ignore non-Jnu elements as quickly as possible. Jnu adds the jnuRecordSet property on every element
   * it renders record data into, and this should be done to dynamically created DOM nodes to get this 
   * behaviour (and others) to work
   */
  if (!ev.target.jnuRecordSet)
    return;
  if (ev.shiftKey || ev.ctrlKey || ev.altKey || ev.metaKey || ev.isComposing)
    return;

  /* Default behaviour of contenteditable elements is to add a line break when the user presses enter.
   * This is not what we want in almost every case imaginable, so we disable it.
   */
  if (ev.key === 'Enter')
  {
    if (ev.target.getAttribute('contenteditable') === 'plaintext-only')
    {
      ev.target.blur();
      ev.preventDefault();
      ev.stopPropagation();
      ev.target.parentElement.focus();
    }
  }
}
window.addEventListener('keydown', globalKeyHandler);

/**
 * Keyboard shortcuts which are applied while a record is chosen. If multiple record sets have multiple
 * records chosen, do our best to take the action on the record set closest to the mouse pointer when
 * the event target isn't a child of a record set.
 *
 * esc   => click the close button
 */
function choiceKeyHandler(ev)
{
  if ((ev.key === 'Escape') && !(ev.shiftKey || ev.ctrlKey || ev.altKey || ev.metaKey || ev.isComposing))
  {
    const closestRs =  ev.target.closest('JNU-RECORD-SET');
    if (closestRs)
      closestRs.querySelector('[name="close-button"]')?.click();
    else
    {
      let rs;
      const chosenRecordSets = $$('JNU-RECORD-SET[jnu-chosen-record');
      const hoverRecordSets = $$('JNU-RECORD-SET:has(*:hover)');

      if (chosenRecordSets.length === 1 || hoverRecordSets.length === 0)
        rs = chosenRecordSets[0];
      else
        rs = hoverRecordSets[0];

      if (rs)
      {
        const closeButtons = rs.querySelectorRelated('[name="close-button"]');
        if (closeButtons.length)
          closeButtons[0].click();
      }
      return;
    }
    ev.target.closest('JNU-RECORD-SET')?.querySelector('[name="close-button"]')?.click();
  }
}

/**
 * Intercept browser's native handling of a hyperlink click and swap in our own navigation code.
 */
function anchorClickHandler(ev)
{
  ev.preventDefault();
  navigateTo(ev.target.getAttribute('href'));
}

/**
 * Navigate to a /xyz-style href which is assumed to be a Vite route implemented with an iframe. /xyz is rewritten
 * as /xyz/index.html, and any querystring is handled correctly.
 *
 * @todo support url hash
 */
export function navigateTo(href)
{
  var qs = '';
  var els = href.split('?');
  if (els.length == 2)
  {
    href = els[0];
    qs = '?' + els[1];
  }

  top.history.pushState(undefined, undefined, href);
  setTimeout(() => {
    domTk.ignoreUncaught();
    window.location.href = href + '/index.html' + qs;
  }, 1);
}

/**
 * Make the browser download a keystore, but first prompt for a password. If the original keystore has a
 * label property, it will be copied into the new keystore.
 *
 * @params {Keystore|PrivateKey} ks
 * @params {String}              filename   the filename to use to store the keystore
 * @params {Object}              options    dcp-modal options
 * @returns {Promise} that is resolved when the download tries to start
 */
async function downloadKeystoreWithNewPassword(ks, filename, options)
{
  const modals = dcp['dom-tk'].modals;
  const username = dcp.wallet.generateKeystorePasswordManagerUsername(ks);
  const passphrase = await modals.passwordPrompt(username, Object.assign({
    prompt: 'Enter passphrase',
    prompt2: 'Confirm passphrase',
    showEye: true,
  }, options, { mode: 'create' }));

  if (passphrase === null)
    return;
  if (passphrase === false)
    return await modals.alert('Passphrases did not match');

  const newKs = await new dcp.wallet.Keystore(await ks.getPrivateKey(), passphrase);
  newKs.label = ks.label || '';
  const contents = JSON.stringify(newKs);
  const a = document.createElement('a');
  a.href = 'data:text/plain;charset=utf-8,' + encodeURIComponent(contents);
  a.download = filename;
  a.style.display = 'none';
  document.body.appendChild(a);
  a.click();
  a.remove();
}

/**
 * Reference-counted "busy" UI hint, depends on portal-iframe.css for behaviour.
 */
export function busy()
{
  document.body.setAttribute('busy', Number(document.body.getAttribute('busy') || '0') + 1);
  return arguments[0];
}
export function unbusy()
{
  const next = Number(document.body.getAttribute('busy') || '0') - 1;
  if (next > 0)
    document.body.setAttribute('busy', next);
  else
    document.body.removeAttribute('busy');
  return arguments[0];
}

domTk = await p$domTk;
if (domTk.modules.ifCommon)
  throw new Error('iframe-common module already loaded!');
domTk.modules.ifCommon = { navigateTo, copyToClipboard, downloadKeystoreWithNewPassword, busy, unbusy };
